import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { rem } from 'polished'
import styled, { css } from 'styled-components'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import Label from 'LuxKit/Label/Label'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import NestableClickableListItem from 'LuxKit/List/NestableClickableListItem'
import productLabels from 'i18n/productLabels'
import type { BookedExperienceSummary, ExperienceTilePrice } from '../hooks/useTripPlanExperienceTileData'
import { PLACEHOLDER_TILE_BORDER_WIDTH } from './constants'
import TripPlanTileDashedOutline, { tripPlanTileOutlineHoverStyles } from './TripPlanTileDashedOutline'

const EXPERIENCE_TILE_ICON_BACKGROUND = 'rgba(30, 140, 140, 0.2)'
const EXPERIENCE_TILE_ICON_COLOUR = '#1E8C8C'

const ExperienceTileContainer = styled(NestableClickableListItem)`
  border: ${rem(PLACEHOLDER_TILE_BORDER_WIDTH)} solid transparent;
  padding: ${rem(12)} ${rem(14)};
  ${tripPlanTileOutlineHoverStyles}

  &.has-saved-experiences {
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.has-saved-experiences:hover,
  &.has-saved-experiences:focus-within {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &&:not(:disabled):hover {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

const ExperienceThumbnail = styled(ListItemThumbnail)<{ $placeholder?: boolean }>`
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: ${rem(10)};

  ${props => props.$placeholder && css`
    &&& {
      background-color: ${EXPERIENCE_TILE_ICON_BACKGROUND};
      color: ${EXPERIENCE_TILE_ICON_COLOUR};
    }
  `}

  svg {
    width: ${rem(16)};
    height: ${rem(16)};
  }
`

const ExperienceTileActions = styled(Group)`
  flex-shrink: 0;
`

const ExperienceBrowseButton = styled(TextButton)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  white-space: nowrap;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  padding: ${rem(8)} ${rem(12)};
  line-height: 1;

  &:hover,
  &:focus-visible {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

interface Props {
  bookedExperienceSummary?: BookedExperienceSummary
  bookedExperienceCount: number
  hasSavedExperiences: boolean
  experienceTilePrice?: ExperienceTilePrice
  onActionsClick: () => void
  onOpen: () => void
  savedExperienceCount: number
}

function TripPlanExperienceTile({
  bookedExperienceSummary,
  bookedExperienceCount,
  hasSavedExperiences,
  experienceTilePrice,
  onActionsClick,
  onOpen,
  savedExperienceCount,
}: Props) {
  const intl = useIntl()

  const experiencesLabel = intl.formatMessage(productLabels.experiences)

  const browseOffersLabel = intl.formatMessage({
    id: 'GT_vU_FDzdjIbvsJZtgi',
    defaultMessage: 'Search experiences',
    description: 'Button label to open the experience offers/search drawer from the trip planner stop details',
  })

  const experienceTilePriceLabel = experienceTilePrice === undefined ? undefined : intl.formatMessage(
    {
      id: 'UwBcCE8twltyMVdADlka',
      defaultMessage: 'From {price}/person',
      description: 'Per-person experience price copy shown on the trip planner stop experience tile',
    },
    { price: <FormatCurrency value={experienceTilePrice.price} currency={experienceTilePrice.currency} format="roundedDollar" /> },
  )

  const canOpenBookedExperienceDetails = !!bookedExperienceSummary?.offerId

  const viewBookedExperienceLabel = intl.formatMessage({
    id: 'VBRDHU3BOfNO8eMqJWh9',
    defaultMessage: 'View booked experience details',
    description: 'Accessible label for the booked experience summary action button that opens experience details',
  })

  const browseExperienceOffersLabel = intl.formatMessage({
    id: '76merFuCa8b0XpQ-E8rd',
    defaultMessage: 'Browse experience offers',
    description: 'Accessible label for the experience empty state overflow action button that opens experience offers',
  })

  const experienceActionsLabel = canOpenBookedExperienceDetails ? viewBookedExperienceLabel : browseExperienceOffersLabel

  const savedExperiencesLabel = intl.formatMessage(
    {
      id: 'deAT4OktdSGmFHKotKKG',
      defaultMessage: '{count, plural, one {# saved} other {# saved}}',
      description: 'Status badge showing the number of saved experiences in a trip planner stop',
    },
    { count: savedExperienceCount },
  )

  const bookedExperiencesLabel = intl.formatMessage(
    {
      id: '9cILNi4ZbWjHLm9-a0P1',
      defaultMessage: '{count, plural, one {# booked} other {# booked}}',
      description: 'Status badge showing the number of booked experiences in a trip planner stop',
    },
    { count: bookedExperienceCount },
  )

  return (
    <ExperienceTileContainer
      className={clsx({ 'has-saved-experiences': hasSavedExperiences || !!bookedExperienceSummary })}
      onClick={onOpen}
      size="medium"
      title={bookedExperienceSummary?.name ?? experiencesLabel}
      titleWeight="bold"
      titleLineClamp={1}
      subtitle={bookedExperienceSummary ? undefined : experienceTilePriceLabel}
      subtitleLineClamp={1}
      startIcon={
        <ExperienceThumbnail
          $placeholder={!bookedExperienceSummary}
          kind={bookedExperienceSummary ? 'neutral' : 'highlight-primary-dark'}
          gutter="none"
        >
          <LineTicketIcon size="XS" />
        </ExperienceThumbnail>
      }
    >
      {!hasSavedExperiences && !bookedExperienceSummary && <TripPlanTileDashedOutline radius="S" />}
      <ExperienceTileActions direction="horizontal" gap={8} verticalAlign="center">
        {bookedExperienceSummary && (
          <StatusLabel variant="confirmation">
            {bookedExperiencesLabel}
          </StatusLabel>
        )}
        {!bookedExperienceSummary && hasSavedExperiences && (
          <Label kind="tertiary" variant="success">
            {savedExperiencesLabel}
          </Label>
        )}
        {!bookedExperienceSummary && !hasSavedExperiences && (
          <ExperienceBrowseButton size="small" kind="tertiary" onClick={onOpen} startIcon={<LineSearchIcon size="XS" />}>
            {browseOffersLabel}
          </ExperienceBrowseButton>
        )}
        <IconButton
          size="small"
          kind="tertiary"
          onClick={onActionsClick}
          aria-label={experienceActionsLabel}
        >
          <LineEllipsisVIcon />
        </IconButton>
      </ExperienceTileActions>
    </ExperienceTileContainer>
  )
}

export default TripPlanExperienceTile
